import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CloudDownload, CloudOff, Eye, Flame, Gauge, Home, Lock, Maximize2, MessageSquareQuote, PlayCircle, RotateCcw, ShieldCheck, Trees } from "lucide-react";

const API_ROOT = process.env.REACT_APP_BACKEND_URL;
const EQUIP_TAG = { keins: "Ohne Equipment", ball: "Mit Fußball", huetchen: "Mit Hütchen", leiter: "Mit Koordinationsleiter", band: "Mit Widerstandsband", wand: "Mit Wand", minitor: "Mit Minitor", matte: "Mit Matte", stange: "Mit Stangen" };

export function exerciseTags(ex) {
  const t = (ex.equipment?.length ? ex.equipment : ["keins"]).map((e) => EQUIP_TAG[e] || e);
  if (ex.locations?.includes("zuhause")) t.push("Für zu Hause");
  if (ex.locations?.some((l) => l === "draussen" || l === "platz")) t.push("Für draußen");
  if (ex.small_space) t.push("Für kleine Räume");
  return t;
}

export const Tags = ({ ex, testId }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testId}>{exerciseTags(ex).map((t) => (
    <span key={t} className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] border border-white/15 text-[#D4D4D8]">
      {t === "Für zu Hause" ? <Home className="w-3 h-3" /> : t === "Für draußen" ? <Trees className="w-3 h-3" /> : t === "Für kleine Räume" ? <Maximize2 className="w-3 h-3" /> : null}{t}</span>
  ))}</div>
);

const fmt = (s) => `${Math.round(s)} Sek.`;

export function VideoPlayer({ ex }) {
  const ref = useRef(null);
  const [ended, setEnded] = useState(false);
  const [slow, setSlow] = useState(false);
  if (ex.locked || !ex.has_video) {
    return (
      <div className="relative aspect-video bg-[#1A1A1A] overflow-hidden" data-testid="exercise-video-placeholder">
        {ex.image_url && <img src={ex.image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
          <div className="w-16 h-16 rounded-full border-2 border-[#c1ff72] flex items-center justify-center bg-black/40">{ex.locked ? <Lock className="w-6 h-6 text-[#c1ff72]" /> : <PlayCircle className="w-7 h-7 text-[#c1ff72]" />}</div>
          <p className="text-xs uppercase tracking-widest text-[#A1A1AA]">{ex.locked ? "Gesperrt" : "Das Video wird gerade vom Trainer geprüft – bis dahin hilft dir die Anleitung unten."}</p>
        </div>
      </div>
    );
  }
  const replay = () => { const v = ref.current; v.currentTime = 0; v.play(); setEnded(false); };
  const toggleSlow = () => { const v = ref.current; v.playbackRate = slow ? 1 : 0.5; setSlow(!slow); };
  return (
    <div className="relative bg-black -mx-5 sm:mx-0" data-testid="exercise-video-wrap">
      <video ref={ref} controls playsInline preload="metadata" poster={ex.poster_url ? `${API_ROOT}${ex.poster_url}` : ex.image_url} onEnded={() => setEnded(true)} onPlay={() => setEnded(false)}
        className="w-full aspect-video object-contain bg-black max-h-[80dvh]" src={`${API_ROOT}/api/training/media/${ex.exercise_id}/video`} data-testid="exercise-video" />
      {ended && <button onClick={replay} className="absolute inset-0 m-auto w-40 h-14 bg-[#c1ff72] text-black font-bold flex items-center justify-center gap-2" data-testid="exercise-video-replay"><RotateCcw className="w-5 h-5" />Nochmal</button>}
      <div className="flex items-center gap-2 px-5 sm:px-0 py-3 bg-[#0A0A0A]">
        {ex.video_duration && <span className="text-xs text-[#A1A1AA]" data-testid="exercise-video-duration">Video · {fmt(ex.video_duration)}</span>}
        <button onClick={toggleSlow} className={`ml-auto h-9 px-3 text-xs border flex items-center gap-1.5 ${slow ? "bg-[#c1ff72] text-black border-[#c1ff72]" : "border-white/20 text-white"}`} data-testid="exercise-video-slow"><Gauge className="w-3.5 h-3.5" />{slow ? "Langsam (0,5×)" : "Langsam ansehen"}</button>
        <OfflineButton ex={ex} />
      </div>
    </div>
  );
}

const swCall = (msg) => new Promise((resolve) => {
  if (!navigator.serviceWorker?.controller) return resolve({ ok: false });
  const ch = new MessageChannel(); ch.port1.onmessage = (e) => resolve(e.data); navigator.serviceWorker.controller.postMessage(msg, [ch.port2]);
  return undefined;
});

function OfflineButton({ ex }) {
  const url = `${API_ROOT}/api/training/media/${ex.exercise_id}/video`;
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (window.caches) caches.open("pft-videos-v1").then((c) => c.match(new URL(url).pathname)).then((h) => setSaved(!!h)); }, [url]);
  if (!("serviceWorker" in navigator) || !window.caches) return null;
  const toggle = async () => {
    setBusy(true);
    const r = await swCall({ type: saved ? "remove-video" : "save-video", url });
    setBusy(false);
    if (!r.ok) return toast.error("Offline-Speichern ist hier nicht möglich. Installiere die App und versuche es erneut.");
    setSaved(!saved); toast.success(saved ? "Offline-Video entfernt" : "Gespeichert – das Video läuft jetzt auch ohne Internet");
    return undefined;
  };
  return <button disabled={busy} onClick={toggle} className="h-9 px-3 text-xs border border-white/20 text-white flex items-center gap-1.5" data-testid="exercise-video-offline">{saved ? <CloudOff className="w-3.5 h-3.5" /> : <CloudDownload className="w-3.5 h-3.5" />}{saved ? "Offline gespeichert" : "Offline speichern"}</button>;
}

export const FocusNote = ({ ex }) => ex.focus_note ? (
  <div className="flex gap-3 border-l-2 border-[#c1ff72] bg-[#c1ff72]/[0.06] p-4 mt-4" data-testid="exercise-focus-note"><Eye className="w-5 h-5 text-[#c1ff72] shrink-0" />
    <div><p className="text-[10px] uppercase tracking-[0.2em] text-[#c1ff72]">Darauf achten</p><p className="text-white mt-1">{ex.focus_note}</p></div></div>
) : null;

export const WarmupNote = ({ ex }) => ex.warmup_note ? (
  <div className="flex gap-3 border border-orange-400/30 bg-orange-400/[0.06] p-4" data-testid="exercise-warmup-note"><Flame className="w-5 h-5 text-orange-300 shrink-0" />
    <div><p className="text-[10px] uppercase tracking-[0.2em] text-orange-300">Aufwärmen</p><p className="text-[#D4D4D8] mt-1">{ex.warmup_note}</p></div></div>
) : null;

export const CoachTip = ({ ex }) => ex.coach_tip ? (
  <div className="bg-[#121212] border border-white/10 p-6" data-testid="exercise-coach-tip"><p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#71717A] mb-2"><MessageSquareQuote className="w-4 h-4 text-[#c1ff72]" />Trainerempfehlung</p>
    <p className="text-white leading-relaxed">„{ex.coach_tip}“</p></div>
) : null;

export const Posture = ({ ex }) => ex.posture?.length ? (
  <div data-testid="exercise-posture"><p className="text-xs uppercase tracking-[0.2em] text-[#71717A] mb-4">Körperhaltung</p>
    <ul className="space-y-3">{ex.posture.map((x) => <li key={x} className="flex gap-3 text-[#D4D4D8]"><ShieldCheck className="w-5 h-5 shrink-0 text-[#c1ff72]" />{x}</li>)}</ul></div>
) : null;

export const SafetyHint = () => (
  <p className="flex gap-2 text-xs text-[#71717A] mt-10" data-testid="exercise-safety"><AlertTriangle className="w-4 h-4 shrink-0" />Bei Schmerzen sofort abbrechen. Kinder und Jugendliche trainieren mit Aufsicht eines Erwachsenen.</p>
);
